// src/utils/dateFormatters.ts

/**
 * Format date to Indian short format: DD-MMM-YYYY
 */
export function formatIndianDate(date: any): string {
  if (!date) return '';
  
  let d: Date;
  
  // Handle Firestore Timestamp
  if (typeof date === 'object' && date.toDate) {
    d = date.toDate();
  } 
  // Handle Date object
  else if (date instanceof Date) {
    d = date;
  }
  // Handle string
  else if (typeof date === 'string') {
    d = new Date(date);
  }
  else {
    return '';
  }
  
  // Check if date is valid
  if (isNaN(d.getTime())) {
    return '';
  }
  
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const day = d.getDate().toString().padStart(2, '0');
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  
  return `${day}-${month}-${year}`;
}

/**
 * Format date to Indian short format with time: DD/MM/YYYY HH:MM
 */
export function formatIndianDateTime(date: any): string {
  if (!date) return '';
  
  let d: Date;
  
  // Handle Firestore Timestamp
  if (typeof date === 'object' && date.toDate) {
    d = date.toDate();
  } 
  // Handle Date object
  else if (date instanceof Date) {
    d = date;
  }
  // Handle string
  else if (typeof date === 'string') {
    d = new Date(date);
  }
  else {
    return '';
  }
  
  // Check if date is valid
  if (isNaN(d.getTime())) {
    return '';
  }
  
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/**
 * Format date for display in tables and cards
 */
export function formatDisplayDate(date: any): string {
  return formatIndianDate(date);
}

/**
 * Formats a date range to Indian format
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Formatted date range (e.g., "01-Oct-2025 to 11-Oct-2025")
 */
export function formatIndianDateRange(
  startDate: Date | { toDate?: () => Date } | string | null | undefined,
  endDate: Date | { toDate?: () => Date } | string | null | undefined
): string {
  const start = formatIndianDate(startDate);
  const end = formatIndianDate(endDate);

  if (start === '' && end === '') return '-';
  if (start === '') return `Until ${end}`;
  if (end === '') return `From ${start}`;

  return `${start} to ${end}`;
}

/**
 * Gets a relative date label for common periods
 * @param dateRange - Date range type from preset
 * @returns Human-readable label (e.g., "Today", "This Month")
 */
export function getDateRangeLabel(dateRange: string): string {
  const labels: Record<string, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisQuarter: 'This Quarter',
    lastQuarter: 'Last Quarter',
    thisYear: 'This Year',
    lastYear: 'Last Year',
    custom: 'Custom Period',
  };

  return labels[dateRange] || dateRange;
}

/**
 * Formats a month and year
 * @param month - Month number (0-11)
 * @param year - Year number
 * @returns Formatted month year (e.g., "Oct 2025")
 */
export function formatMonthYear(month: number, year: number): string {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[month]} ${year}`;
}

/**
 * Formats a week range
 * @param startDate - Week start date
 * @returns Formatted week (e.g., "Week of 08-Oct-2025")
 */
export function formatWeek(startDate: Date | { toDate?: () => Date } | string | null | undefined): string {
  const start = formatIndianDate(startDate);
  return start === '' ? '-' : `Week of ${start}`;
}
